import { useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, MapPin, Clock, Globe, Send, ArrowRight } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Contact form submitted:", formData);
    // Handle form submission here
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-blue-100">
              Get in touch with our engineering team for your marine and industrial needs
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">G5 Petroleum Sdn Bhd</h2>
                <p className="text-lg text-gray-700 mb-8">
                  Engineering & Marine Supply Solutions across Southeast Asia. Contact our experienced team for reliable marine hoses, industrial hoses, pig launcher systems, and ship brokering services.
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-6">
                <Card className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                        <a href="https://wa.me/60192177871" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800 font-medium transition-colors">+60 19 2177871</a>
                        <p className="text-sm text-gray-500">Available during business hours</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Office Phone</h3>
                        <a href="tel:+60327790507" className="text-blue-600 hover:text-blue-800 font-medium transition-colors">+603 2779 0507</a>
                        <p className="text-sm text-gray-500">Main office line</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                        <a href="mailto:sales@g5petro.com.my" className="text-blue-600 hover:text-blue-800 font-medium transition-colors">sales@g5petro.com.my</a>
                        <p className="text-sm text-gray-500">For sales inquiries and quotations</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                        <p className="text-gray-700">Ampang Jaya, Selangor</p>
                        <p className="text-gray-700">Malaysia</p>
                        <p className="text-sm text-gray-500">Serving Southeast Asia</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Working Hours</h3>
                        <p className="text-gray-700">Mon–Fri: 9:00am – 6:00pm</p>
                        <p className="text-gray-700">Sat: By appointment</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Service Areas */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Malaysia",
                    "Singapore", 
                    "Indonesia",
                    "Vietnam",
                    "Thailand",
                    "Middle East"
                  ].map((area, index) => (
                    <Badge key={index} variant="outline" className="px-3 py-1">
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and our team will get back to you within 24 hours
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          placeholder="Your full name"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="company">Company *</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleInputChange("company", e.target.value)}
                          placeholder="Company name"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder="your.email@company.com"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          placeholder="+60 xxx xxx xxxx"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="service">Product/Service Interest</Label>
                      <Select value={formData.service} onValueChange={(value) => handleInputChange("service", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="marine-hose">Floating Marine Hose Systems</SelectItem>
                          <SelectItem value="industrial-hose">Flexible Industrial Hose</SelectItem>
                          <SelectItem value="pig-launcher">Pig Launcher & Receiver Systems</SelectItem>
                          <SelectItem value="ship-brokering">Ship Brokering & Marine Agent</SelectItem>
                          <SelectItem value="consultation">Engineering Consultation</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange("message", e.target.value)}
                        className="min-h-[120px]"
                        placeholder="Please describe your requirements, project details, or any specific questions..."
                        required
                      />
                    </div>
                    
                    <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
                      <Send className="mr-2 h-5 w-5" />
                      Submit Now
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Company Profile Download */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="shadow-lg border-0">
              <CardContent className="p-8">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <Globe className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Download Company Profile</h2>
                <p className="text-gray-700 mb-6">
                  Get detailed information about our services, capabilities, and track record in marine and industrial engineering solutions.
                </p>
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4">
                  Download PDF Profile
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quick Contact Options */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Need Immediate Assistance?</h2>
            <p className="text-xl text-blue-100">Our team is ready to help with your urgent requirements</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
              <CardContent className="p-6">
                <Phone className="h-8 w-8 text-white mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">WhatsApp Now</h3>
                <p className="text-blue-100 mb-4">Message us directly</p>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900">
                  <a href="https://wa.me/60192177871" target="_blank" rel="noopener noreferrer">+60 19 2177871</a>
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
              <CardContent className="p-6">
                <Phone className="h-8 w-8 text-white mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">Office Phone</h3>
                <p className="text-blue-100 mb-4">Call our main office</p>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900">
                  <a href="tel:+60327790507">+603 2779 0507</a>
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
              <CardContent className="p-6">
                <Mail className="h-8 w-8 text-white mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">Email Us</h3>
                <p className="text-blue-100 mb-4">Quick response guaranteed</p>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900">
                  <a href="mailto:sales@g5petro.com.my">sales@g5petro.com.my</a>
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
              <CardContent className="p-6">
                <Globe className="h-8 w-8 text-white mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">WhatsApp</h3>
                <p className="text-blue-100 mb-4">Instant messaging support</p>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900">
                  Message Us
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Contact;